import { api } from '../../core/services/api';
import { config } from '../../core/config/env';
import type { ActionOption, ChatMessage, ChatSession, ChatStreamEvent, ChatViewContext, ModelOption, ToolOption } from '../types';

interface OneEnvelope<T> {
  data: T;
}
interface ManyEnvelope<T> {
  data: T[];
}

/**
 * Single swappable boundary for everything the assistant needs from the
 * backend — REST for sessions/messages, a hand-rolled SSE reader for the
 * streaming reply. See AI-Assistant-Feature-Backend.md §8/§9 for the
 * contract this implements.
 */
export const aiChatService = {
  async getConfig(): Promise<{ models: ModelOption[]; tools: ToolOption[]; actions: ActionOption[] }> {
    const res = await api.get<OneEnvelope<{ models: ModelOption[]; tools: ToolOption[]; actions: ActionOption[] }>>(
      '/ai-assistant/config',
    );
    return res.data.data;
  },

  async listSessions(): Promise<ChatSession[]> {
    const res = await api.get<ManyEnvelope<ChatSession>>('/ai-assistant/sessions', {
      params: { sort: '-lastMessageAt', page_size: 100 },
    });
    return res.data.data;
  },

  /**
   * `projectId` closes the gap so the backend's session system prompt (§3) has
   * a project to frame itself around. `assistantRoleKey` binds the session to
   * a role-based AI Assistant (docs/AI-Assistant-Roles.md §5.4) — omit for the
   * general assistant.
   */
  async createSession(projectId?: number | null, assistantRoleKey?: string | null): Promise<ChatSession> {
    const res = await api.post<OneEnvelope<ChatSession>>('/ai-assistant/sessions', {
      ...(projectId != null ? { projectId } : {}),
      ...(assistantRoleKey ? { assistantRoleKey } : {}),
    });
    return res.data.data;
  },

  async getSession(id: number): Promise<ChatSession> {
    const res = await api.get<OneEnvelope<ChatSession>>(`/ai-assistant/sessions/${id}`);
    return res.data.data;
  },

  async renameSession(id: number, title: string): Promise<ChatSession> {
    const res = await api.put<OneEnvelope<ChatSession>>(`/ai-assistant/sessions/${id}`, { title });
    return res.data.data;
  },

  async deleteSession(id: number): Promise<void> {
    await api.delete(`/ai-assistant/sessions/${id}`);
  },

  async listMessages(sessionId: number): Promise<ChatMessage[]> {
    const res = await api.get<ManyEnvelope<ChatMessage>>(`/ai-assistant/sessions/${sessionId}/messages`);
    return res.data.data;
  },

  async sendMessage(
    sessionId: number,
    content: string,
    viewContext?: ChatViewContext,
  ): Promise<{
    userMessageId: number;
    assistantMessageId: number;
    messageCount: number;
    approachingLimit: boolean;
    /** Present only when this call generated it — the session's first message. */
    title?: string;
  }> {
    const res = await api.post<
      OneEnvelope<{
        userMessageId: number;
        assistantMessageId: number;
        messageCount: number;
        approachingLimit: boolean;
        title?: string;
      }>
    >(`/ai-assistant/sessions/${sessionId}/messages`, { content, ...(viewContext ? { viewContext } : {}) });
    return res.data.data;
  },

  async cancelMessage(messageId: number): Promise<void> {
    await api.post(`/ai-assistant/messages/${messageId}/cancel`);
  },

  async regenerateMessage(
    messageId: number,
  ): Promise<{ assistantMessageId: number; messageCount: number; approachingLimit: boolean }> {
    const res = await api.post<OneEnvelope<{ assistantMessageId: number; messageCount: number; approachingLimit: boolean }>>(
      `/ai-assistant/messages/${messageId}/regenerate`,
    );
    return res.data.data;
  },

  /** Backend-executed lane (§6.1) — resumes a paused tool call so it actually runs. */
  async approveToolCall(messageId: number, toolUseId: string): Promise<ChatMessage> {
    const res = await api.post<OneEnvelope<ChatMessage>>(
      `/ai-assistant/messages/${messageId}/tool-calls/${toolUseId}/approve`,
    );
    return res.data.data;
  },

  /** Backend-executed lane (§6.1) — denies a paused tool call. */
  async rejectToolCall(messageId: number, toolUseId: string): Promise<ChatMessage> {
    const res = await api.post<OneEnvelope<ChatMessage>>(
      `/ai-assistant/messages/${messageId}/tool-calls/${toolUseId}/reject`,
    );
    return res.data.data;
  },

  /** Frontend-executed lane (§6.2) — records the decision only, after the caller has already performed (or dismissed) the action itself. */
  async decideToolCall(messageId: number, toolUseId: string, decision: 'approved' | 'rejected'): Promise<ChatMessage> {
    const res = await api.post<OneEnvelope<ChatMessage>>(
      `/ai-assistant/messages/${messageId}/tool-calls/${toolUseId}/decision`,
      { decision },
    );
    return res.data.data;
  },

  /**
   * Opens the SSE stream for one assistant message. Uses `fetch` +
   * `ReadableStream` rather than `EventSource` because `EventSource` can't
   * send an `Authorization` header, and every `/api/v1` route requires one.
   */
  async streamMessage(
    messageId: number,
    onEvent: (event: ChatStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = getAccessToken();
    const res = await fetch(`${config.apiBaseUrl}/ai-assistant/messages/${messageId}/stream`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`Stream request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let separatorIndex: number;
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const event = parseSseBlock(block);
        if (event) onEvent(event);
      }
    }
  },
};

/** The `data:` line is the full JSON event (it already carries `kind`) — the separate `event:` line is redundant for our purposes. */
function parseSseBlock(block: string): ChatStreamEvent | null {
  const dataLine = block.split('\n').find((line) => line.startsWith('data:'));
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine.slice(5).trim()) as ChatStreamEvent;
  } catch {
    return null;
  }
}

/** Mirrors AuthContext's own localStorage read — kept local so this module has no dependency beyond the storage key/shape. */
function getAccessToken(): string | null {
  try {
    const raw = localStorage.getItem(config.authStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { tokens?: { accessToken?: string } };
    return parsed.tokens?.accessToken ?? null;
  } catch {
    return null;
  }
}
